import type { ResponseError } from "app/services/fetchService";

import { SALESLOFT_FORBIDDEN_STATUS, type SalesloftCadenceVisibility } from "../constants/salesloftConstants";
import { useSalesloftCadencesQuery } from "../queries/useSalesloftCadencesQuery";
import type { SalesloftCadence } from "../types/salesloft.types";

import { useSalesloftConnect } from "./useSalesloftConnect";

type UseSalesloftCadencesParams = {
    enabled?: boolean;
    name?: string;
    visibility?: SalesloftCadenceVisibility;
    includeSteps?: boolean;
    /** Called after the user reconnects Salesloft and cadences load successfully. */
    onReconnected?: () => void;
};

type UseSalesloftCadencesResult = {
    cadences: SalesloftCadence[];
    isLoading: boolean;
    isError: boolean;
    isReconnectRequired: boolean;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => Promise<unknown>;
    refetch: () => void;
    openSalesloftConnect: () => void;
};

/**
 * Single entry point for the Salesloft cadence picker: bundles the paginated cadences query,
 * the reconnect flow, and infinite-scroll paging. A 403 means the Salesloft connection expired
 * (`isReconnectRequired`), and `openSalesloftConnect` opens the OAuth popup then refetches —
 * invoking `onReconnected` once cadences load again.
 */
export const useSalesloftCadences = ({
    enabled = true,
    name,
    visibility,
    includeSteps,
    onReconnected,
}: UseSalesloftCadencesParams = {}): UseSalesloftCadencesResult => {
    const { cadences, isLoading, isError, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
        useSalesloftCadencesQuery({ enabled, name, visibility, includeSteps });

    const isReconnectRequired = (error as ResponseError | null)?.status === SALESLOFT_FORBIDDEN_STATUS;

    const { openSalesloftConnect } = useSalesloftConnect({
        onWindowClosed: async () => {
            const result = await refetch();
            if (result.data && !result.error) {
                onReconnected?.();
            }
        },
    });

    return {
        cadences,
        isLoading,
        isError: isError && !isReconnectRequired,
        isReconnectRequired,
        hasNextPage: Boolean(hasNextPage),
        isFetchingNextPage,
        fetchNextPage: () => fetchNextPage(),
        refetch: () => void refetch(),
        openSalesloftConnect,
    };
};
